import { Box, type CSS, Flex, styled } from '@kandji-inc/nectar-ui';
import { InterfaceContext } from 'contexts/interface';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useWhiteBg from 'src/features/util/hooks/use-white-bg';
import { useBodyNoMargin } from 'src/hooks/useBodyNoMargin';
import { useHubspotHandler } from 'src/hooks/useHubspotHandler';

type PageLayoutProps = {
  breadcrumbs?: React.ReactNode;
  header: React.ReactNode;
  content?: React.ReactNode;
  contentWithTabs?: React.ReactNode;
  pageContentCss?: CSS;
};

const StyledPageLayout = styled(Flex, {
  margin: '0 calc(var(--k-main-layout-side-padding) * -1) -48px',
  height: 'calc(100vh - var(--k-banner-height))',
  position: 'relative',
  padding: '$4 $5 0 $5',
});

const StyledPageBreadcrumbs = styled(Flex, {
  variants: {
    hasContent: {
      true: {
        paddingBottom: '$3',
      },
    },
  },
});

const StyledPageHeader = styled(Flex, {
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingBottom: '$3',
  position: 'sticky',
});

const StyledPageContentWithTabs = styled(Box, {
  variants: {
    hasContent: {
      true: {
        marginLeft: '-$5',
        marginRight: '-$5',
      },
    },
  },
});

const StyledPageContent = styled(Box, {
  overflow: 'auto',
  paddingTop: '$3',
  paddingLeft: '$5',
  paddingRight: '$5',
  variants: {
    hasContent: {
      true: {
        borderTop: '1px solid $neutral30',
        marginLeft: '-$5',
        marginRight: '-$5',
      },
    },
  },
});

const PageLayout = (props: PageLayoutProps) => {
  useWhiteBg();
  useBodyNoMargin();
  useHubspotHandler();

  const [distanceOffset, setDistanceOffset] = useState(null);
  const { bannerTopOffset } = useContext(InterfaceContext);

  const getDistanceOffset = useCallback(() => {
    const bannerElement = document.getElementById('UniversalAlertsLayer');
    const pageHeaderElement = document.querySelector('[data-page-header]');
    const { height: headerHeight } = pageHeaderElement?.getBoundingClientRect();

    if (bannerElement) {
      return bannerTopOffset + headerHeight - 32;
    }

    return headerHeight + 32;
  }, [bannerTopOffset]);

  const pageContentCss = useMemo(
    () => ({
      height: `calc(100vh - ${distanceOffset}px - var(--k-main-layout-side-padding))`,
      ...props.pageContentCss,
    }),
    [distanceOffset, props.pageContentCss],
  );

  useEffect(() => {
    setDistanceOffset(getDistanceOffset());
  }, [getDistanceOffset]);

  return (
    <StyledPageLayout flow="column" id="page-layout" data-testid="page-layout">
      <Box data-page-header>
        <StyledPageBreadcrumbs hasContent={!!props.breadcrumbs}>
          {props.breadcrumbs}
        </StyledPageBreadcrumbs>

        <StyledPageHeader>{props.header}</StyledPageHeader>
      </Box>

      <StyledPageContentWithTabs hasContent={!!props.contentWithTabs}>
        {props.contentWithTabs}
      </StyledPageContentWithTabs>

      <StyledPageContent
        hasContent={!!props.content}
        css={!!props.content && pageContentCss}
        data-testid="page-layout-content"
      >
        {props.content}
      </StyledPageContent>
    </StyledPageLayout>
  );
};

export default PageLayout;
